import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Aperture, Menu, X, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { label: "Vision", target: "vision", testid: "nav-link-vision" },
  { label: "How It Works", target: "how-it-works", testid: "nav-link-how-it-works" },
  { label: "Coming Soon", target: "early-product", testid: "nav-link-coming-soon" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, target) => {
    e.preventDefault();
    setOpen(false);
    scrollToId(target);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled ? "border-b border-white/5 bg-[#090909]/80 backdrop-blur-md" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <a
          href="#top"
          onClick={(e) => go(e, "top")}
          className="group flex items-center gap-2.5"
          data-testid="nav-logo"
        >
          <Aperture
            className="h-5 w-5 text-[#D4AF37] transition-transform duration-700 group-hover:rotate-90"
            strokeWidth={1.5}
          />
          <span className="font-display text-lg font-bold tracking-tight text-[#F5F5F5]">
            pitsora
          </span>
        </a>

        <nav className="hidden items-center gap-9 md:flex" aria-label="Primary">
          {LINKS.map((link) => (
            <a
              key={link.target}
              href={`#${link.target}`}
              onClick={(e) => go(e, link.target)}
              className="group relative font-mono text-[11px] uppercase tracking-[0.22em] text-[#9A9A9A] transition-colors duration-300 hover:text-[#F5F5F5]"
              data-testid={link.testid}
            >
              {link.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-[#D4AF37] transition-[width] duration-400 group-hover:w-full" />
            </a>
          ))}
          <button
            onClick={(e) => go(e, "waitlist")}
            className="group flex items-center gap-1.5 border border-[#D4AF37]/50 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] transition-colors duration-300 hover:bg-[#D4AF37] hover:text-[#090909]"
            data-testid="nav-cta-waitlist"
          >
            Join the Waitlist
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </nav>

        <button
          className="text-[#F5F5F5] md:hidden"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-b border-white/5 bg-[#090909]/95 backdrop-blur-md md:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-6 px-6 py-8">
              {LINKS.map((link) => (
                <a
                  key={link.target}
                  href={`#${link.target}`}
                  onClick={(e) => go(e, link.target)}
                  className="font-mono text-xs uppercase tracking-[0.25em] text-[#9A9A9A]"
                  data-testid={`${link.testid}-mobile`}
                >
                  {link.label}
                </a>
              ))}
              <button
                onClick={(e) => go(e, "waitlist")}
                className="mt-2 border border-[#D4AF37]/50 px-5 py-3 text-left font-mono text-xs uppercase tracking-[0.25em] text-[#D4AF37]"
                data-testid="nav-cta-waitlist-mobile"
              >
                Join the Waitlist
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
